import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ErrorBoundary from "./components/common/ErrorBoundary";
import Login from "./components/Login";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import PropTypes from "prop-types";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GOOGLE_CLIENT_ID } from "./config/api";
import { ToastContainer } from "./components/ui/Toast";

// Lazy load components for better performance
const Dashboard = lazy(() => import("./components/Dashboard"));
const Leads = lazy(() => import("./pages/Leads"));
const Clients = lazy(() => import("./pages/Clients"));
const Employees = lazy(() => import("./pages/Employees"));
const PropertyPage = lazy(() => import("./pages/PropertyPage"));
const Opportunity = lazy(() => import("./pages/opportunity"));
const Profile = lazy(() => import("./pages/profile"));
const Report = lazy(() => import("./pages/Report"));
const Updateopportunity = lazy(() => import("./pages/Updateopportunity"));
const Data = lazy(() => import("./components/data"));
const Test = lazy(() => import("./pages/test"));
const Newopp = lazy(() => import("./pages/Newopp"));
const Chat = lazy(() => import("./components/Chat"));
const RRport = lazy(() => import("./components/rrport"));
const Pipeline = lazy(() => import("./pages/Pipeline"));
const TaskManagement = lazy(() => import("./pages/TaskManagement"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const AIInsights = lazy(() => import("./pages/AIInsights"));
const AIAgentHub = lazy(() => import("./pages/AIAgentHub"));
const AIApprovals = lazy(() => import("./pages/AIApprovals"));
const PropertyPendingApprovals = lazy(() => import("./pages/PropertyPendingApprovals"));
const IntelligenceDashboard = lazy(() => import("./pages/IntelligenceDashboard"));
const LeadJourney = lazy(() => import("./pages/LeadJourney"));
const ExceptionCenter = lazy(() => import("./pages/ExceptionCenter"));
const PublicLeadForm = lazy(() => import("./pages/PublicLeadForm"));
const PublicProposalPage = lazy(() => import("./PublicProposalPage"));

// Loading component for suspense fallback
const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "200px",
      fontSize: "16px",
      color: "#666",
    }}
  >
    <div>Loading...</div>
  </div>
);

const AppLayout = ({ children }) => (
  <>
    <Navbar />
    <div className="layout">
      <Sidebar />
      <div className="content">
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
        </ErrorBoundary>
      </div>
    </div>
  </>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem("authToken") ? true : false;
  return isLoggedIn ? children : <Navigate to="/" />;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// Main App Component
const App = () => {
  const isLoggedIn = localStorage.getItem("authToken") ? true : false;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ErrorBoundary>
        <Router>
          <Routes>
            {/* Redirect to dashboard if logged in */}
            <Route
              path="/"
              element={isLoggedIn ? <Navigate to="/dashboard" /> : <Login />}
            />
            <Route
              path="/login"
              element={isLoggedIn ? <Navigate to="/dashboard" /> : <Login />}
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Leads />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Employees />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Clients />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PropertyPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Chat />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/rrport"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <RRport />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/pipeline"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Pipeline />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/opportunity"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Opportunity />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/newopp"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Newopp />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/report"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Report />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/updateopportunity"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Updateopportunity />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Profile />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/data"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Data />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/test"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Test />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TaskManagement />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AuditLogs />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-insights"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIInsights />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-agent"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AIAgentHub />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-approvals"
              element={<Navigate to="/ai-agent" replace />}
            />
            <Route
              path="/property-submissions"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PropertyPendingApprovals />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/intelligence"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <IntelligenceDashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/exceptions"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ExceptionCenter />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/journey/:correlationId"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <LeadJourney />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inquiry"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <PublicLeadForm />
                  </Suspense>
                </ErrorBoundary>
              }
            />
            <Route
              path="/proposal/public/:opportunityId"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <PublicProposalPage />
                  </Suspense>
                </ErrorBoundary>
              }
            />
          </Routes>
        </Router>
      </ErrorBoundary>
      <ToastContainer position="top-right" maxToasts={5} />
    </GoogleOAuthProvider>
  );
};

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default App;
